  import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './slider.js';

declare global {
  interface Window {
    audioService: any;
  }
}

@customElement('mixer-controls')
export class MixerControls extends LitElement {
  @state() private crossfaderValue = 0.5;
  @state() private masterVolume = 0.8;
  @state() private isPlaying = false;

  protected createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  protected firstUpdated(): void {
    if (window.audioService) {
      window.audioService.addEventListener(this.handleAudioEvent.bind(this));
    }
    this.setupMediaSession();
  }

  private handleAudioEvent(event: string, data: any): void {
    switch (event) {
      case 'crossfaderChanged':
        this.crossfaderValue = data.value;
        break;
      case 'masterVolumeChanged':
        this.masterVolume = data.value;
        break;
    }
  }

  private updateCrossfader(value: number): void {
    this.crossfaderValue = value;
    if (window.audioService) {
      window.audioService.setCrossfaderPosition(value);
    }
  }

  private updateMasterVolume(value: number): void {
    this.masterVolume = value;
    if (window.audioService) {
      window.audioService.setMasterVolume(value);
    }
  }

  private togglePlayPause(): void {
    // Find all audio players and toggle their playback
    const audioPlayers = document.querySelectorAll('audio-player');
    audioPlayers.forEach((player: any) => {
      if (player && player.play) {
        player.play();
      }
    });
    
    // Toggle local state
    this.isPlaying = !this.isPlaying;
    this.updateMediaSession();
  }

  render() {
    return html`
      <div class="mixer-container">
        <button class="play-pause-button" @click=${this.togglePlayPause}>
          ${this.isPlaying ? 
            html`<svg width="40" height="40" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>` :
            html`<svg width="40" height="40" viewBox="0 0 24 24" fill="white">
              <polygon points="8,5 8,19 19,12"/>
            </svg>`
          }
        </button>
        <div class="control-section">
          <h2>Balance</h2>
          <div class="crossfader-container">
            <div class="slider-container">
              <div class="slider-text-row justify-between">
                <span class="text-left">Recording</span>
                <span class="text-right">Track</span>
              </div>
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${1 - this.crossfaderValue}
                    min="0"
                    max="1"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateCrossfader(1 - e.detail.value)}
                  ></app-slider>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="control-section">
          <h2>Volume</h2>
          
          <div class="volume-container">
            <div class="slider-container">
              <div class="slider-row">
                <div class="slider-track">
                  <app-slider
                    .value=${this.masterVolume}
                    min="0"
                    max="1"
                    step="0.01"
                    height="20"
                    foregroundColor="white"
                    backgroundColor="rgba(255, 255, 255, 0.1)"
                    @input=${(e: CustomEvent) => this.updateMasterVolume(e.detail.value)}
                  ></app-slider>
                </div>
              </div>
              <div class="slider-text-row justify-center">
                <span class="value-display">${(this.masterVolume * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private setupMediaSession(): void {
    if (!('mediaSession' in navigator)) {
      console.log('Media Session API not supported');
      return;
    }

    // Set up action handlers
    navigator.mediaSession.setActionHandler('play', () => {
      this.togglePlayPause();
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      this.togglePlayPause();
    });

    navigator.mediaSession.setActionHandler('previoustrack', () => {
      this.previousTrack();
    });

    navigator.mediaSession.setActionHandler('nexttrack', () => {
      this.nextTrack();
    });

    // this.updateMediaSession();
  }

  private updateMediaSession(): void {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: "Ambient Singapore",
      artist: "listen.sg",
      album: '',
    });

    navigator.mediaSession.playbackState = this.isPlaying ? 'playing' : 'paused';
    console.log('Media Session playback state:', navigator.mediaSession.playbackState);
  }

  private previousTrack(): void {
    const audioPlayers = document.querySelectorAll('audio-player');
    audioPlayers.forEach((player: any) => {
      if (player) {
        player.playPreviousTrack();
      }
    });
  }

  private nextTrack(): void {
    const audioPlayers = document.querySelectorAll('audio-player');
    audioPlayers.forEach((player: any) => {
      if (player) {
        player.playNextTrack();
      }
    });
  }
}